import { NavLink, useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import {
  Folder,
  GitBranch,
  Workflow,
  Calendar as CalIcon,
  Settings,
  Plus,
  Activity,
  Flame,
} from "lucide-react";
import { listProjects, listSessions } from "../lib/api";
import { fmtRelative } from "../lib/format";

/**
 * Inner sidebar contents — used by both the persistent desktop sidebar
 * and the mobile drawer. `onNavigate` is called whenever a nav link is
 * tapped, so the mobile drawer can close itself.
 */
export default function SidebarContent({ refreshKey = 0, onNavigate }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [streakSessions, setStreakSessions] = useState([]);
  const navigate = useNavigate();
  const { projectId } = useParams();

  useEffect(() => {
    let active = true;
    setLoading(true);
    listProjects()
      .then((p) => {
        if (active) setProjects(p);
      })
      .catch(() => {})
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [refreshKey]);

  useEffect(() => {
    let active = true;
    if (!projectId) {
      setStreakSessions([]);
      return;
    }
    listSessions(projectId)
      .then((s) => {
        if (active) setStreakSessions(s || []);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [projectId, refreshKey]);

  const streak = useMemo(() => computeStreak(streakSessions), [streakSessions]);

  const navItem = (to, label, Icon, testid) => (
    <NavLink
      key={to}
      to={to}
      data-testid={testid}
      end
      onClick={() => onNavigate?.()}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all duration-200 ${
          isActive
            ? "bg-white border border-[#e5e7eb] text-[#111827] shadow-sm"
            : "text-[#6b7280] hover:bg-white/60 hover:text-[#111827]"
        }`
      }
    >
      <Icon size={16} strokeWidth={1.6} />
      <span>{label}</span>
    </NavLink>
  );

  return (
    <div className="flex flex-col gap-6 h-full">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center">
          <Activity size={16} className="text-white" strokeWidth={2.2} />
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-[15px] font-semibold tracking-tight text-[#111827]">Trace</span>
          <span className="text-[11px] text-[#6b7280]">Trace how you built it.</span>
        </div>
      </div>

      <nav className="flex flex-col gap-1">
        {navItem("/", "Projects", Folder, "nav-projects")}
        {projectId && (
          <>
            {navItem(`/project/${projectId}`, "Resume", GitBranch, "nav-resume")}
            {navItem(`/project/${projectId}/flow`, "Flow View", Workflow, "nav-flow")}
            {navItem(`/project/${projectId}/timeline`, "Timeline", CalIcon, "nav-timeline")}
          </>
        )}
        {navItem("/settings", "Settings", Settings, "nav-settings")}
      </nav>

      <div className="mt-2">
        <div className="flex items-center justify-between px-1 mb-2">
          <span className="text-[11px] uppercase tracking-wider text-[#6b7280]">Projects</span>
          <button
            data-testid="sidebar-add-project"
            onClick={() => {
              onNavigate?.();
              navigate("/onboarding");
            }}
            className="text-[#6b7280] hover:text-[#111827] transition"
            aria-label="Add project"
          >
            <Plus size={14} />
          </button>
        </div>

        {loading ? (
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-9 rounded-md bg-[#eef0f3] animate-pulse" />
            ))}
          </div>
        ) : projects.length === 0 ? (
          <button
            data-testid="sidebar-empty-cta"
            onClick={() => {
              onNavigate?.();
              navigate("/onboarding");
            }}
            className="w-full text-left px-3 py-3 rounded-md border border-dashed border-[#e5e7eb] text-xs text-[#6b7280] hover:bg-white transition"
          >
            Connect your first repo →
          </button>
        ) : (
          <ul className="space-y-1">
            {projects.map((p) => {
              const active = p.id === projectId;
              return (
                <li key={p.id}>
                  <NavLink
                    to={`/project/${p.id}`}
                    data-testid={`sidebar-project-${p.id}`}
                    onClick={() => onNavigate?.()}
                    className={`block px-3 py-2 rounded-md transition-all duration-200 ${
                      active
                        ? "bg-white border border-[#e5e7eb] shadow-sm"
                        : "hover:bg-white/60"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-[#111827] truncate">{p.name}</span>
                      {active && (
                        <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-blue-500 to-indigo-500" />
                      )}
                    </div>
                    <div className="text-[11px] text-[#6b7280] truncate font-mono">
                      {p.full_name}
                    </div>
                    <div className="text-[10px] text-[#9ca3af] mt-0.5">
                      {p.last_active_at ? `active ${fmtRelative(p.last_active_at)}` : "no activity yet"}
                    </div>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {projectId && streak.thisWeek > 0 && (
        <div
          data-testid="streak-card"
          className="mt-auto rounded-xl border border-[#e5e7eb] bg-white p-3.5 trace-fade-in"
          title={`${streak.thisWeek} sessions in the last 7 days · ${streak.thisMonth} this month`}
        >
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded-md bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center text-white">
              <Flame size={14} strokeWidth={2.2} />
            </span>
            <div className="leading-tight">
              <div className="text-[13px] font-semibold text-[#111827] tabular-nums">
                {streak.thisWeek}{" "}
                <span className="font-normal text-[#6b7280]">
                  session{streak.thisWeek === 1 ? "" : "s"} this week
                </span>
              </div>
              <div className="text-[10px] text-[#6b7280] font-medium">
                {streak.streakDays >= 3
                  ? `${streak.streakDays}-day streak · You're in flow`
                  : streak.streakDays > 1
                  ? `${streak.streakDays}-day streak`
                  : streak.lastActiveLabel}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className={`${projectId && streak.thisWeek > 0 ? "mt-3" : "mt-auto"} text-[10px] text-[#9ca3af] px-1`}>
        Local-first project memory · v0.1
      </div>
    </div>
  );
}

function computeStreak(sessions) {
  const empty = { thisWeek: 0, thisMonth: 0, streakDays: 0, lastActiveLabel: "no streak yet" };
  if (!sessions || sessions.length === 0) return empty;
  const now = Date.now();
  const week = now - 7 * 86400 * 1000;
  const month = now - 30 * 86400 * 1000;

  let thisWeek = 0;
  let thisMonth = 0;
  const dayKeys = new Set();
  let last = 0;

  for (const s of sessions) {
    const t = new Date(s.end_time).getTime();
    if (Number.isNaN(t)) continue;
    if (t >= week) thisWeek += 1;
    if (t >= month) thisMonth += 1;
    if (t > last) last = t;
    dayKeys.add(new Date(t).toISOString().slice(0, 10));
  }

  const refDate = new Date(last || now);
  let streakDays = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const k = refDate.toISOString().slice(0, 10);
    if (dayKeys.has(k)) {
      streakDays += 1;
      refDate.setUTCDate(refDate.getUTCDate() - 1);
    } else {
      break;
    }
  }

  return {
    thisWeek,
    thisMonth,
    streakDays,
    lastActiveLabel: last ? "keep it going" : "no streak yet",
  };
}
